import { DOCUMENT_CATEGORY } from '../invoicing/enums';
import type { DocumentCategory } from '../invoicing/enums';
import type { BillingStatusInvoice, BillingStatusItem } from '../invoicing/internal-api';

/**
 * What an extra fee is (SWOOP-416). A fee's `name` is free text an admin can rename or repeat, so rules that
 * act on one kind of fee (re-billing the stamp duty) read this code instead of matching the name. A territory
 * holds at most one fee per code other than `OTHER`.
 */
export const CONTRACT_FEE_CODE = {
  NOTARISATION: 'NOTARISATION',
  OTHER: 'OTHER',
  STAMP_DUTY: 'STAMP_DUTY',
} as const;

export type ContractFeeCode = (typeof CONTRACT_FEE_CODE)[keyof typeof CONTRACT_FEE_CODE];

export const CONTRACT_FEE_CODES: ContractFeeCode[] = Object.values(CONTRACT_FEE_CODE);

/**
 * What the code-level registry says about each fee code (SWOOP-416):
 * - `rebillable`: the fee can be invoiced to the customer on its own, as a fee re-bill;
 * - `referenceCode`: the two upper-case letters of the request reference (`SD/2026/000001`, the ERP number format of
 *   the legal invoices); never an invoice document code (BC, DM, AJ, CR); null when the fee has no reference;
 * - `documentCategory`: the invoicing category its invoice is raised under; null when it is never invoiced.
 */
export const CONTRACT_FEE_CODE_REGISTRY: Record<
  ContractFeeCode,
  { documentCategory: DocumentCategory | null; rebillable: boolean; referenceCode: string | null }
> = {
  [CONTRACT_FEE_CODE.NOTARISATION]: {
    documentCategory: DOCUMENT_CATEGORY.ADJUSTMENT,
    rebillable: false,
    referenceCode: 'NT',
  },
  [CONTRACT_FEE_CODE.OTHER]: { documentCategory: null, rebillable: false, referenceCode: null },
  [CONTRACT_FEE_CODE.STAMP_DUTY]: {
    documentCategory: DOCUMENT_CATEGORY.ADJUSTMENT,
    rebillable: true,
    referenceCode: 'SD',
  },
};

/** The fee codes a re-bill can be generated for. */
export const REBILLABLE_FEE_CODES: ContractFeeCode[] = CONTRACT_FEE_CODES.filter(
  (code) => CONTRACT_FEE_CODE_REGISTRY[code].rebillable,
);

/**
 * The source reference of ONE fee re-bill request, `<contractId>:<feeCode>:<reference>` (for example
 * `665f…:STAMP_DUTY:SD/2026/000001`). A fee can be re-billed in several requests until it is fully covered, and each
 * request is its own billable and charge key, so its own reference is part of the key. The invoicing provider and the
 * status read-back both use it, so a request and its invoice always meet on the same key.
 */
export function feeRebillSourceRef(contractId: string, feeCode: ContractFeeCode, reference: string): string {
  return `${contractId}:${feeCode}:${reference}`;
}

/**
 * The invoice a source ref is on, read from what invoicing answered: the KNOWN item of that `sourceRef` (an UNKNOWN
 * one has no charges to read), then the first of its charges that is on an invoice; `null` when invoicing does not
 * know the ref, or none of its charges has been put on an invoice yet. The one rule both the contract's Stamp duty
 * section and its Invoices section read a re-bill request's invoice by, so they can never disagree on which invoice
 * a request is on.
 */
export function billedInvoiceOfSourceRef(
  items: readonly BillingStatusItem[],
  sourceRef: string,
): BillingStatusInvoice | null {
  const item = items.find((candidate) => candidate.sourceRef === sourceRef && candidate.status === 'KNOWN');
  return item?.charges.find((charge) => charge.invoice !== null)?.invoice ?? null;
}

const FEE_REBILL_SOURCE_REF_PATTERN = /^([0-9a-f]{24}):([A-Z_]+):([A-Z]{2}\/\d{4}\/\d{6})$/;

/**
 * Reads a {@link feeRebillSourceRef} back into its parts; `null` when the text is not one (an agreement id, for
 * example, is a plain hex id), which is how the invoicing provider tells a fee request from an agreement.
 */
export function parseFeeRebillSourceRef(
  sourceRef: string,
): { contractId: string; feeCode: ContractFeeCode; reference: string } | null {
  const match = FEE_REBILL_SOURCE_REF_PATTERN.exec(sourceRef);
  if (match === null) {
    return null;
  }
  const [, contractId, code, reference] = match;
  const feeCode = CONTRACT_FEE_CODES.find((candidate) => candidate === code);
  return feeCode === undefined ? null : { contractId, feeCode, reference };
}
